import { useEffect, useMemo, useRef, useState } from 'react';

export const AD_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || '';
export const ADSENSE_SCRIPT_SRC = AD_CLIENT
  ? `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`
  : '';
export const PRIVACY_CONTACT_EMAIL =
  process.env.REACT_APP_PRIVACY_CONTACT_EMAIL || 'privacy@jobspostedtoday.com';

const ADSENSE_SCRIPT_SELECTOR = ADSENSE_SCRIPT_SRC ? `script[src="${ADSENSE_SCRIPT_SRC}"]` : '';
const FUNDING_CHOICES_FALLBACK_TIMEOUT_MS = 4500;

const US_STATES_STATUS = {
  UNKNOWN: 0,
  DOES_NOT_APPLY: 1,
  NOT_OPTED_OUT: 2,
  OPTED_OUT: 3,
};

const CONSENT_MODE_STATUS = {
  UNKNOWN: 0,
  GRANTED: 1,
  DENIED: 2,
  NOT_APPLICABLE: 3,
  NOT_CONFIGURED: 4,
};

const getConsentModeStatusValues = (googleFcConsentModeStatus) => [
  googleFcConsentModeStatus?.adStoragePurposeConsentStatus,
  googleFcConsentModeStatus?.adUserDataPurposeConsentStatus,
  googleFcConsentModeStatus?.adPersonalizationPurposeConsentStatus,
  googleFcConsentModeStatus?.analyticsStoragePurposeConsentStatus,
];

const hasConsentModeSignal = (googleFcConsentModeStatus) =>
  getConsentModeStatusValues(googleFcConsentModeStatus).some(
    (consentModeValue) => consentModeValue !== undefined && consentModeValue !== null
  );

export const canServeAdsFromConsentMode = (googleFcConsentModeStatus) => {
  const allConsentModeValues = getConsentModeStatusValues(googleFcConsentModeStatus);
  if (!allConsentModeValues.length) return false;

  for (const consentModeValue of allConsentModeValues) {
    switch (consentModeValue) {
      case CONSENT_MODE_STATUS.UNKNOWN:
      case CONSENT_MODE_STATUS.DENIED:
      case undefined:
      case null:
        return false;
      case CONSENT_MODE_STATUS.GRANTED:
      case CONSENT_MODE_STATUS.NOT_APPLICABLE:
      case CONSENT_MODE_STATUS.NOT_CONFIGURED:
        break;
      default:
        return false;
    }
  }

  return true;
};

export const ensureGoogleFcGlobals = () => {
  if (typeof window === 'undefined') return null;

  window.googlefc = window.googlefc || {};
  window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
  window.googlefc.usstatesoptout = window.googlefc.usstatesoptout || {};
  window.googlefc.usstatesoptout.overrideDnsLink = true;

  return window.googlefc;
};

export const ensureAdSenseScript = () => {
  if (typeof document === 'undefined' || !ADSENSE_SCRIPT_SRC) return null;

  const existing = ADSENSE_SCRIPT_SELECTOR
    ? document.querySelector(ADSENSE_SCRIPT_SELECTOR)
    : null;
  if (existing) return existing;

  const script = document.createElement('script');
  script.async = true;
  script.src = ADSENSE_SCRIPT_SRC;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
  return script;
};

const pushGoogleFcCallback = (key, callback) => {
  const googlefc = ensureGoogleFcGlobals();
  if (!googlefc?.callbackQueue || typeof callback !== 'function') return false;
  googlefc.callbackQueue.push({ [key]: callback });
  return true;
};

export const openPrivacyChoicesDialog = () => {
  if (typeof window === 'undefined') return false;

  const canOpenEuDialog = typeof window.googlefc?.showRevocationMessage === 'function';
  if (canOpenEuDialog) {
    window.googlefc.showRevocationMessage();
    return true;
  }

  const canOpenUsDialog =
    typeof window.googlefc?.usstatesoptout?.openConfirmationDialog === 'function';
  if (canOpenUsDialog) {
    window.googlefc.usstatesoptout.openConfirmationDialog(() => {});
    return true;
  }

  const queuedEu = pushGoogleFcCallback('CONSENT_API_READY', () => {
    if (typeof window.googlefc?.showRevocationMessage === 'function') {
      window.googlefc.showRevocationMessage();
    }
  });
  const queuedUs = pushGoogleFcCallback('INITIAL_US_STATES_OPT_OUT_DATA_READY', () => {
    if (typeof window.googlefc?.usstatesoptout?.openConfirmationDialog === 'function') {
      window.googlefc.usstatesoptout.openConfirmationDialog(() => {});
    }
  });

  return queuedEu || queuedUs;
};

export const useMonetizationGate = () => {
  const [adsPermission, setAdsPermission] = useState(AD_CLIENT ? 'pending' : 'disabled');
  const [privacyChoicesKind, setPrivacyChoicesKind] = useState('none');
  const [consentModeResolved, setConsentModeResolved] = useState(false);

  const euAppliesRef = useRef(null);
  const consentResolvedRef = useRef(false);

  useEffect(() => {
    if (!AD_CLIENT || typeof window === 'undefined') return undefined;

    ensureGoogleFcGlobals();
    ensureAdSenseScript();

    let cancelled = false;

    const updateAdsPermission = (nextPermission) => {
      if (cancelled) return;
      setAdsPermission((current) => (current === nextPermission ? current : nextPermission));
    };

    const showEuPrivacyChoices = () => {
      if (cancelled) return;
      setPrivacyChoicesKind('eu');
    };

    const showUsPrivacyChoices = () => {
      if (cancelled) return;
      setPrivacyChoicesKind((current) => (current === 'eu' ? current : 'us'));
    };

    pushGoogleFcCallback('CONSENT_API_READY', () => {
      if (typeof window.__tcfapi !== 'function') {
        euAppliesRef.current = false;
        return;
      }

      window.__tcfapi('addEventListener', 0, (tcData, success) => {
        if (!success || !tcData) return;

        if (tcData.gdprApplies === true) {
          euAppliesRef.current = true;
          showEuPrivacyChoices();
          return;
        }

        if (tcData.gdprApplies === false) {
          euAppliesRef.current = false;
          updateAdsPermission('allowed');
        }
      });
    });

    pushGoogleFcCallback('CONSENT_MODE_DATA_READY', () => {
      const consentModeStatus = window.googlefc?.getGoogleConsentModeValues?.();
      if (!hasConsentModeSignal(consentModeStatus)) {
        return;
      }

      consentResolvedRef.current = true;
      if (!cancelled) {
        setConsentModeResolved(true);
      }

      if (canServeAdsFromConsentMode(consentModeStatus)) {
        updateAdsPermission('allowed');
      } else {
        updateAdsPermission('blocked');
      }
    });

    pushGoogleFcCallback('INITIAL_US_STATES_OPT_OUT_DATA_READY', () => {
      const usStatesStatus =
        window.googlefc?.usstatesoptout?.getInitialUsStatesOptOutStatus?.();

      if (
        usStatesStatus === US_STATES_STATUS.NOT_OPTED_OUT ||
        usStatesStatus === US_STATES_STATUS.OPTED_OUT
      ) {
        showUsPrivacyChoices();
      }

      if (euAppliesRef.current !== true && usStatesStatus !== US_STATES_STATUS.UNKNOWN) {
        updateAdsPermission('allowed');
      }
    });

    const fallbackTimer = window.setTimeout(() => {
      if (cancelled) return;
      if (consentResolvedRef.current) return;
      if (euAppliesRef.current === true) return;
      updateAdsPermission('allowed');
    }, FUNDING_CHOICES_FALLBACK_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  return useMemo(
    () => ({
      adsPermission,
      canServeAds: adsPermission === 'allowed',
      privacyChoicesKind,
      hasPrivacyChoices: privacyChoicesKind !== 'none',
      consentModeResolved,
      openPrivacyChoices: openPrivacyChoicesDialog,
    }),
    [adsPermission, consentModeResolved, privacyChoicesKind]
  );
};
